<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  MAX_BUTTONS,
  MAX_LIST_ROWS,
  MAX_BUTTON_TITLE,
  MAX_ROW_TITLE,
  resolvePresentation,
} from './constants';

const props = defineProps({
  modelValue: { type: Array, required: true },
  presentation: { type: String, required: true },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const resolvedType = computed(() =>
  resolvePresentation(props.presentation, props.modelValue)
);

const isButtonMode = computed(() => resolvedType.value === 'button');

const limit = computed(() =>
  isButtonMode.value ? MAX_BUTTONS : MAX_LIST_ROWS
);

const titleLimit = computed(() =>
  isButtonMode.value ? MAX_BUTTON_TITLE : MAX_ROW_TITLE
);

const overLimit = computed(() => props.modelValue.length > limit.value);

const hasOverlongTitle = computed(() =>
  props.modelValue.some(
    option => (option.title || '').length > titleLimit.value
  )
);

const updateOption = (index, key, value) => {
  const next = props.modelValue.map((option, i) =>
    i === index ? { ...option, [key]: value } : option
  );
  emit('update:modelValue', next);
};

const addOption = () => {
  emit('update:modelValue', [
    ...props.modelValue,
    { title: '', value: '', description: '', section: '' },
  ]);
};

const removeOption = index => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index)
  );
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_INTERACTIVE.FORM.OPTIONS') }}
      </label>
      <span class="text-xs text-n-slate-11">
        {{ modelValue.length }} / {{ limit }}
      </span>
    </div>

    <div
      v-for="(option, index) in modelValue"
      :key="index"
      class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
    >
      <div class="flex gap-2">
        <Input
          :model-value="option.title"
          class="flex-1"
          size="sm"
          :placeholder="t('WHATSAPP_INTERACTIVE.FORM.OPTION_TITLE')"
          @update:model-value="value => updateOption(index, 'title', value)"
        />
        <Input
          :model-value="option.value"
          class="flex-1"
          size="sm"
          :placeholder="t('WHATSAPP_INTERACTIVE.FORM.OPTION_VALUE')"
          @update:model-value="value => updateOption(index, 'value', value)"
        />
        <Button
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          :aria-label="t('WHATSAPP_INTERACTIVE.FORM.REMOVE_OPTION')"
          @click="removeOption(index)"
        />
      </div>
      <!-- Descriptions and sections only render in list mode, so they are hidden
           while the message would go out as reply buttons. -->
      <div v-if="!isButtonMode" class="flex gap-2">
        <Input
          :model-value="option.description"
          class="flex-1"
          size="sm"
          :placeholder="t('WHATSAPP_INTERACTIVE.FORM.OPTION_DESCRIPTION')"
          @update:model-value="
            value => updateOption(index, 'description', value)
          "
        />
        <Input
          :model-value="option.section"
          class="flex-1"
          size="sm"
          :placeholder="t('WHATSAPP_INTERACTIVE.FORM.OPTION_SECTION')"
          @update:model-value="value => updateOption(index, 'section', value)"
        />
      </div>
    </div>

    <div class="flex items-center gap-3">
      <Button
        variant="faded"
        color="slate"
        size="sm"
        :label="t('WHATSAPP_INTERACTIVE.FORM.ADD_OPTION')"
        @click="addOption"
      />
      <span v-if="overLimit" class="text-xs text-n-ruby-9">
        {{
          isButtonMode
            ? t('WHATSAPP_INTERACTIVE.FORM.BUTTON_LIMIT')
            : t('WHATSAPP_INTERACTIVE.FORM.LIST_LIMIT')
        }}
      </span>
      <span v-else-if="hasOverlongTitle" class="text-xs text-n-amber-11">
        {{ t('WHATSAPP_INTERACTIVE.FORM.TITLE_TOO_LONG') }}
      </span>
    </div>
  </div>
</template>
